import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightArrowLeft, faShieldCheck } from '@fortawesome/pro-solid-svg-icons';
import Link from '@v2/shared/components/Link';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

/** The panel describes the link it hangs off; this line renders once per page. */
const TOOLTIP_ID = 'guarantees-satisfaction';

/**
 * The reassurance line under the buy buttons: a hoverable satisfaction guarantee and a
 * returns window, each pointing at the promise on /guarantee that it is the short form of.
 *
 * headless built the tooltip out of a HeadlessUI Popover it then had to open on hover by
 * synthesizing a click on its own button and an Escape keypress to close it — a Popover
 * is a click-to-open control and it was being used as a hover card. This does the same
 * thing with the state it actually needs, which also makes it work on a keyboard: the
 * trigger focuses, and focus opens the panel the way hover does.
 *
 * Both badges are links rather than a button and a span, because both are claims the
 * guarantee page states in full and a reader who wants the terms needs somewhere to
 * click. The tooltip is then supplementary rather than the only route to the detail,
 * which is why it hangs off `aria-describedby`: the trigger's job is to navigate, so
 * `aria-expanded` would be describing a disclosure control that no longer exists. On a
 * phone, where there is no hover, the tap that used to open the tooltip now follows the
 * link — the terms rather than the summary of them.
 *
 * The paths are hand-written, so they go through `buildUrl` the way `Authors` does: AU
 * serves from a path segment and a bare `/guarantee` would drop it.
 */
export default function Guarantees() {
  const [isOpen, setIsOpen] = useState(false);
  const { buildUrl } = useUrlGenerator();

  return (
    <div className="flex justify-center gap-5 mb-3">
      <div
        className="relative"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <Link
          href={buildUrl('/guarantee#satisfaction')}
          className="group align-middle"
          aria-describedby={isOpen ? TOOLTIP_ID : undefined}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
        >
          <span className="flex items-center justify-center cursor-pointer">
            <FontAwesomeIcon
              icon={faShieldCheck}
              className="mr-1.5 text-red-600 group-hover:text-red-700 group-active:text-red-800"
              size="lg"
            />
            <span className="font-semibold text-slate-700 hover:text-slate-900 text-sm italic border-b border-dashed border-b-slate-300 group-hover:border-b-slate-900">
              Satisfaction Guaranteed
            </span>
          </span>
        </Link>

        {isOpen && (
          <div
            id={TOOLTIP_ID}
            role="tooltip"
            className="absolute z-10 bottom-9 w-64 sm:w-80 left-1/2 -translate-x-1/2"
          >
            <div className="bg-slate-900/90 text-white p-2 rounded text-sm text-left">
              Whether it&apos;s our A rated BBB accreditation or our Elite Seller status with Reseller
              Ratings, you can trust that we&apos;ll do whatever it takes to take care of you. We&apos;ve
              got over 40,000 verified reviews, and 1M+ satisfied customers!
            </div>
          </div>
        )}
      </div>

      <Link
        href={buildUrl('/guarantee#books')}
        className="group flex items-center justify-center"
      >
        <FontAwesomeIcon icon={faArrowRightArrowLeft} className="mr-1.5 text-blue-500" size="lg" />
        <span className="font-semibold text-slate-700 group-hover:text-slate-900 text-sm italic">
          30-Day Returns
        </span>
      </Link>
    </div>
  );
}
